<template lang="pug">
  #member-profile-settings
    MemberSettingsFormWrapper(
      :form-data="formData"
      :disable-save.sync="disableSave"
    )
      ContentLoader(:width="300" :height="80" v-if="!memberLoaded")
        rect(x="0", y="0", rx="4", ry="4", width="23%", height="75")
        rect(x="25%", y="0", rx="4", ry="4", width="23%", height="23")
        rect(x="25%", y="25", rx="4", ry="4", width="23%", height="23")
        rect(x="25%", y="50", rx="4", ry="4", width="23%", height="23")
        rect(x="50%", y="0", rx="4", ry="4", width="23%", height="23")
        rect(x="50%", y="25", rx="4", ry="4", width="23%", height="23")
        rect(x="50%", y="50", rx="4", ry="4", width="23%", height="23")
        rect(x="75%", y="0", rx="4", ry="4", width="23%", height="23")
        rect(x="75%", y="25", rx="4", ry="4", width="23%", height="23")
        rect(x="75%", y="50", rx="4", ry="4", width="23%", height="23")

      .columns(v-else)
        .column.is-full-mobile.is-one-quarter.is-centered.custom-quarter-column(v-if="memberLoaded")
          WtUploadImage(
            entity="program"
            :currentImage.sync="currentAvatar"
            @updateImage="newAvatar = $event"
            @saveImage="uploadAvatar"
            show-save
            :loading="uploadInProgress"
          )
            template(v-slot:label)
              WtLabel(isLabelElement text="Upload Your Profile Photo")
        .column
          .notification.is-warning(v-if="isConnectedProgram") Some fields are disabled by your program. Contact your program admin if any changes are needed.
          .columns.is-multiline
            .column.is-half
              BField(
                label="First Name"
                label-for="firstName"
                :type="{ 'is-danger': errors.has('first name') }"
                :message="errors.first('first name')"
              )
                BInput#firstName(
                  type="text"
                  name="first name"
                  v-model="firstName"
                  placeholder="First Name"
                  v-validate="'required'"
                  :disabled="isConnectedProgram"
                  @input="disableSave = isOriginalValue('firstName', firstName)"
                )
            .column.is-half
              BField(
                label="Last Name"
                label-for="lastName"
                :type="{ 'is-danger': errors.has('location') }"
                :message="errors.first('last name')"
              )
                BInput#lastName(
                  type="text"
                  name="last name"
                  v-model="lastName"
                  placeholder="Last name"
                  v-validate="'required'"
                  :disabled="isConnectedProgram"
                  @input="disableSave = isOriginalValue('lastName', lastName)"
                )
            .column.is-half
              //- TODO don't want UniquenessCheck with starting value to trigger validation, v-if is a workaround
              UniquenessCheck(
                v-if="memberLoaded && !_.isEmpty(memberCopy)"
                label="Email Address"
                entity="memberEmail"
                placeholder="member@site.com"
                v-model="email"
                :disabled="isConnectedProgram"
                @validName="disableSave = !$event"
              )
            .column.is-half
              BField(label="Timezone" label-for="timezone")
                WtTimezoneSelect(
                  :timezone="timezone"
                  :initial-offset="memberCopy.timezone"
                  @update="updateTimezone($event)"
                )
            .column.is-half
              BField(
                label="Location"
                label-for="location"
                :type="{ 'is-danger': errors.has('location') }"
                :message="errors.first('location')"
              )
                BInput#location(
                  type="text"
                  name="location"
                  v-model="location"
                  placeholder="Location"
                  v-validate="'max:100'"
                  @input="disableSave = isOriginalValue('location', location)"
                )
            .column.is-half(v-if="coreProgramMeta.requireEmployeeId")
              BField(
                :label="coreProgramMeta.employeeIdTitle"
                label-for="employee-id"
              )
                BInput#employee-id(
                  disabled
                  type="text"
                  v-model="memberCopy.employeeId"
                )
            .column.is-half(v-if="!isConnectedProgram")
              BField(label="Password")
                WtButton(
                  is-text
                  @click.native="passwordModalOpen = true"
                ) Change Password

    BModal(
      :active.sync="passwordModalOpen"
      :width="480"
    )
      ChangePasswordModal(@passwordModalOpen="passwordModalOpen = $event")
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ContentLoader } from "vue-content-loader";
import WtButton from "@@/WtButton/WtButton";
import WtTile from "@@/WtTile/WtTile";
import WtTimezoneSelect from "@@/WtTimezoneSelect/WtTimezoneSelect";
import WtTitle from "@@/WtTitle/WtTitle";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";
import WtUploadImage from "@@/WtUploadImage/WtUploadImage";
import memberSettingsFormMixin from "@/mixins/memberSettingsFormMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import MemberSettingsFormWrapper from "@@/MemberSettings/MemberSettingsFormWrapper";
import ChangePasswordModal from "@@/MemberSettings/ChangePasswordModal";
import EntityAvatar from "@@/WtEntityAvatar/WtEntityAvatar";
import WtLabel from "@@/WtLabel/WtLabel";

export default {
  name: "MemberProfileSettings",

  components: {
    WtLabel,
    WtButton,
    WtTile,
    WtTitle,
    WtTimezoneSelect,
    UniquenessCheck,
    WtUploadImage,
    EntityAvatar,
    ChangePasswordModal,
    MemberSettingsFormWrapper,
    ContentLoader
  },

  mixins: [analyticsTrackingMixin, memberSettingsFormMixin],

  data() {
    return {
      currentAvatar: null,
      newAvatar: null,
      uploadInProgress: false,
      timezone: {},
      originalTimezone: null,
      // keysToReset: ["username", "firstName", "email", "lastName", "location"],
      passwordModalOpen: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberCopy", "memberLoaded"]),
    ...mapGetters("program", ["coreProgramMeta", "programSettingsCopy"]),

    isConnectedProgram() {
      return this.programSettingsCopy.isConnectedProgram;
    },

    formData() {
      const data = {
        firstName: this.firstName,
        lastName: this.lastName,
        timezone: this.timezone.value,
        location: this.location
      };

      if (!this.isOriginalValue("email", this.email)) {
        data.email = this.email;
      }

      return data || {};
    },

    firstName: {
      set(firstName) {
        this.updateMemberCopy({ key: "firstName", value: firstName });
      },
      get() {
        return this.memberCopy.firstName;
      }
    },

    lastName: {
      set(lastName) {
        this.updateMemberCopy({ key: "lastName", value: lastName });
      },
      get() {
        return this.memberCopy.lastName;
      }
    },

    email: {
      set(email) {
        this.updateMemberCopy({ key: "email", value: email });
      },
      get() {
        return this.memberCopy.email;
      }
    },

    location: {
      set(location) {
        this.updateMemberCopy({ key: "location", value: location });
      },
      get() {
        return this.memberCopy.location;
      }
    },

    avatar() {
      return this.memberCopy.avatar;
    }
  },

  watch: {
    timezone(newTimezone) {
      if (this.originalTimezone) {
        return;
      }

      this.originalTimezone = newTimezone;
    },

    memberCopy: {
      handler: function() {
        // set the currentAvatar to pass to child components, changes via memberLoaded or updateMember
        this.currentAvatar = this.memberCopy.avatar;
      },
      deep: true
    }
  },

  mounted() {
    this.trackFeatureView("settings", "settings_profile");
    // set the currentAvatar to pass to child components, needs to wait for memberLoaded
    this.currentAvatar = this.memberCopy.avatar;
  },

  methods: {
    ...mapActions("members", ["updateMemberCopy", "updateMember"]),
    ...mapActions(["snackBar"]),

    updateTimezone(timezone) {
      if (!timezone) return this.resetTimezone();
      this.timezone = timezone;
      this.disableSave = this.isOriginalValue("timezone", timezone.value);
    },

    resetTimezone() {
      this.timezone = this.originalTimezone;
      this.disableSave = true;
    },

    async uploadAvatar() {
      let payload = {};
      const formData = new FormData();
      this.uploadInProgress = true;

      formData.set("image", this.newAvatar);

      payload.formData = formData;
      payload.memberId = this.$auth.user().id;
      // TODO refactor updateMember to throw in catch so we can use try/catch here
      await this.updateMember(payload)
        .then(response => {
          if (response.error) {
            const fields = response.error.fields;
            const firstErrorKey = Object.keys(fields)[0];
            let errorMessage = fields[firstErrorKey][0];
            this.snackBar({
              message: "There was an error: " + errorMessage,
              type: "is-danger"
            });
            this.currentAvatar = this.memberCopy.avatar;
          } else {
            this.snackBar({ message: "Your profile image has been updated." });
          }
        })
        .catch(error => {
          if (error.error) {
            console.log("error.error: ", error.error);
          }
        });

      // for member avatars, we rely on s3 to replace the old avatar with the new one,
      // We keep previewImageData in state to reflect the new image immediately, because s3 takes a few seconds.
      this.uploadInProgress = false;
    }
  }
};
</script>

<style lang="sass" scoped>
.custom-quarter-column
  min-width: 240px
</style>
